import { ComponentType, createContext, ReactNode, useContext, useEffect, useState } from 'react'

import setCountry from '@/store/user/actions/setCountry'
import useDirectories from '@/utils/useDirectories'
import usePatreonTier from '@/utils/usePatreonTier'

// This is to adjust the Universal Video Player (UVP) position to account for scrollbar width
const observeUVP = () => {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        if (window.document.getElementById('universalPlayer_wrapper')) {
          const uvp = window.document.getElementById('universalPlayer_wrapper')
          if (!uvp) return
          console.log('[MV] UVP styles: ', uvp.style)
          const originalOverflow = window.document.documentElement.style.overflow
          window.document.documentElement.style.overflow = 'scroll'
          const scollbarWidth = window.innerWidth - window.document.documentElement.clientWidth
          window.document.documentElement.style.overflow = originalOverflow
          uvp.style.right = `${scollbarWidth}px`
          console.log(`[MV] Set UVP right:${scollbarWidth}px`)
          observer.disconnect() // Stop observing after the element is found
          return
        }
      }
    }
  })
  observer.observe(window.document.body, { childList: true, subtree: true })
}

const removeMediavine = () => {
  removeMediavineContainers()
  removeMediavineVariables()
}

const removeMediavineContainers = () =>
  ['#fixed_container_bottom'].forEach((id) =>
    document.querySelectorAll(id)?.forEach((el) => {
      el.remove()
      console.log(`[MV] Removed MV container: ${id}`)
    }),
  )

const removeMediavineVariables = () =>
  ['$adManagementConfig', '$mediavine', 'Fides'].forEach((key) => {
    if (key in window) {
      delete window[key as any]
      console.log(`[MV] Removed MV variable: ${key}`)
    }
  })

export type AdsContextProps = {
  country?: string
  loadAds?: boolean
  region?: string
}

const AdsContext = createContext<AdsContextProps | undefined>(undefined)

type AdsProviderProps = {
  children: ReactNode
  initCountry?: string
  initRegion?: string
}

export const AdsProvider = ({ children, initCountry, initRegion }: AdsProviderProps) => {
  const { query } = useDirectories()
  const patreonTier = usePatreonTier()
  const [isClient, setIsClient] = useState(false)
  const [country, _setCountry] = useState(initCountry)
  const [region, _setRegion] = useState(initRegion)

  const loadAds = isClient ? !patreonTier || !!query?.ads : undefined

  useEffect(() => {
    if (!isClient) setIsClient(typeof window !== 'undefined')
    if (country) setCountry(country) // For Analytics
  }, [])

  // Remove Mediavine ads if user has Patreon tier, unless ads query is present
  useEffect(() => {
    if (loadAds) {
      observeUVP()
    } else {
      removeMediavine()
    }
  }, [loadAds])

  // Remove Mediavine ads on route change, which Mediavine does already but sometimes fails
  // Disabled on 1/8/2026 as it was preventing mobile adhesion + video from loading on subsequent route changes
  /* useEffect(() => {
    const handleRouteChange = () => {
      removeMediavineContainers()
      observeUVP()
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => router.events.off('routeChangeStart', handleRouteChange)
  }, [router.events]) */

  return <AdsContext.Provider value={{ country, loadAds, region }}>{children}</AdsContext.Provider>
}

export const useAds = () => {
  const context = useContext(AdsContext)
  if (!context) {
    throw new Error('useAds must be used within an AdsProvider')
  }
  return context
}

export const withAds =
  <T extends object>(WrappedComponent: ComponentType<T>) =>
  // eslint-disable-next-line react/display-name
  (props: T) => {
    const { country, loadAds, region } = useAds()
    return <WrappedComponent {...props} country={country} loadAds={loadAds} region={region} />
  }
